import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';

import { useRunRowRead, useRunSummary } from '../hooks/use-run';
import { titleSourceFromSummary, useRunTitle } from '../hooks/use-run-title';
import { shortId } from '../utils/curation-format';
import type { NavContextSearch } from '../utils/nav-context';

interface Props {
  runId: string;
  className?: string | undefined;
  /** `{ from, back }` for a link leaving a page under a different origin than its ambient one
   *  (e.g. the failure-patterns view, S12) — omitted keeps today's no-context link. */
  search?: NavContextSearch | undefined;
}

/**
 * The run's display name (exactly as the run header says it), or null while it loads / when it
 * cannot be read. Read from the batched summaries row (every link on a page shares one request);
 * an older catalog-service without that route falls back to one limited run read per run.
 */
function useRunName(runId: string): { name: string | null; isLoading: boolean } {
  const summary = useRunSummary(runId);
  const onFallback = summary.data?.available === false;
  const run = useRunRowRead(runId, onFallback);
  const fromSummary =
    summary.data?.available && summary.data.summary
      ? titleSourceFromSummary(summary.data.summary)
      : undefined;
  const source = onFallback ? run.data : fromSummary;
  const { title } = useRunTitle(source, undefined);
  return {
    name: source ? title : null,
    isLoading: onFallback ? run.isLoading : summary.isLoading,
  };
}

/**
 * G5 — a run's name as plain text (for places that are already a button, e.g. a version row);
 * the short id on hover, and as the fallback when the run cannot be read.
 */
export function RunNameText({ runId, className }: Props) {
  const { t } = useTranslation('catalog-enrichment');
  const { name } = useRunName(runId);
  return (
    <span title={t('runName.hover', { id: shortId(runId) })} className={className}>
      {name ?? t('runName.fallback', { id: shortId(runId) })}
    </span>
  );
}

/**
 * G5 — a run referenced by NAME ("<experiment> · run" / the run's name / its flow), linking to
 * the run; the short id only on hover. The single run-name link for every catalog-enrichment screen.
 */
export function RunNameLink({ runId, className, search }: Props) {
  const { t } = useTranslation('catalog-enrichment');
  const { name, isLoading } = useRunName(runId);

  return (
    <Link
      to="/catalog-enrichment/runs/$runId"
      params={{ runId }}
      {...(search ? { search } : {})}
      title={t('runName.hover', { id: shortId(runId) })}
      className={cn('text-primary min-w-0 cursor-pointer hover:underline', className)}
    >
      {isLoading ? (
        <Skeleton className="inline-block h-4 w-32 align-middle" />
      ) : (
        // The run could not be read: fall back to its short id rather than an empty link.
        (name ?? t('runName.fallback', { id: shortId(runId) }))
      )}
    </Link>
  );
}
